import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import styled from "@emotion/styled"
import { css } from "@emotion/react"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"

import getMediaQuery from "css/breakpoints"
import * as button from "css/buttons"
import * as text from "css/text"

import { useVariations } from "components/family-variations/Variations"
import { Icon } from "components/icon/Icon"
import WishlistButton from "components/wishlist-button/WishlistButton"

const Header = styled.header`
  display: grid;
  grid-template-rows: [title] auto [case] auto 1.875rem [discover] auto 1fr;
  ${getMediaQuery("l")} {
    grid-template-rows: minmax(0px, max-content);
  }
  z-index: 1;
  /* outline: 1px solid magenta; */
  pointer-events: auto;

  grid-column: col 1 / span 6;
  grid-row: infos;

  ${getMediaQuery("m")} {
    grid-column: col 3 / span 6;
    grid-row: 3;
  }

  ${getMediaQuery("l")} {
    grid-row: infos;
    grid-column: col 2 / span 4;
    width: 75%;
  }

  & > * {
    display: inline-block;
  }

  & > div:first-of-type,
  & > div:last-of-type {
    height: 100%;
  }

  & div:last-of-type .hidden {
    position: absolute;
    left: -100vw;
    visibility: hidden;
  }

  .wishlist-button-root {
    position: absolute;
    top: 0;

    margin-block-start: 2rem;

    ${getMediaQuery("m")} {
      margin-block-start: 30%;
    }

    ${getMediaQuery("l")} {
      position: relative;
    }

    .newmodel {
      ${getMediaQuery("l")} {
        position: absolute;
        top: -1.5rem;
      }
    }
  }

  & div:first-of-type > button {
    align-items: center;
    height: fit-content;
    white-space: nowrap;

    ${getMediaQuery("l")} {
      display: none;
      margin-block-end: 6.25rem;
      display: flex;
      grid-row: start;
      grid-column: 1;

      & > span {
        ${text.legend80}
        ${text.bold}
        display: block;
      }
    }
  }

  & div:last-of-type > h2 {
    ${text.headline50}
    ${text.bold}
    grid-row: title;
    grid-column: 1;
    align-self: flex-end;
    line-height: 1.1;

    ${getMediaQuery("l")} {
      grid-row: 1;
    }
  }

  & > div:last-of-type > h3 {
    ${text.body50};
    ${text.light};
    grid-row: case;
    grid-column: 1;
    min-height: 4rem;

    ${getMediaQuery("m")} {
      min-height: auto;
    }
    ${getMediaQuery("l")} {
      grid-row: 1;
    }
  }

  & > div:last-of-type > a {
    ${button.buttonText}
    ${button.green}
    grid-column: 1;
    grid-row: discover;
    margin-inline-start: -1.875rem;
    align-self: end;
    margin-block-end: 2rem;

    ${getMediaQuery("m")} {
      margin-inline-start: -2.1875rem;
    }
  }

  & > div:last-of-type > p {
    opacity: 0;
    pointer-events: none;
  }

  div {
    html.prefers-contrast & {
      color: black !important;
      /* position: relative; */
      span,
      button,
      div,
      p,
      a,
      svg {
        color: black !important;
        fill: black !important;
      }

      ::after {
        background: white;
        content: "";
        height: calc(100% + 30px);
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 20px);
        z-index: -1;
      }
    }
  }
`

const A11yKeyMessage = styled(motion.div)`
  & p {
    margin-block-end: 10px;
    ${text.legend80}
    ${text.bold}
    color: rgb(var(--green));
  }

  .dark-theme & p {
    color: white;
  }
`

const keyStyles = css`
  display: inline-block;
  position: relative;
  width: 36px;
  height: 36px;
  border: 1px solid;
  border-radius: 5px;

  [dir="rtl"] & {
    transform: rotateY(180deg);
  }

  border-color: rgb(var(--green));
  .dark-theme & {
    border-color: white;
  }

  &::after {
    content: "";
    position: absolute;
    width: 10px;
    height: 8px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    background: rgb(var(--green));
    .dark-theme & {
      background: white;
    }
  }
`

const KeyIconLeft = styled.div`
  ${keyStyles}
  &::after {
    clip-path: polygon(0% 50%, 100% 0%, 100% 100%);
  }
  margin-inline-end: 5px;
`
const KeyIconRight = styled.div`
  ${keyStyles}
  &::after {
    clip-path: polygon(0% 0%, 100% 50%, 0% 100%);
  }
`

export default function Infos() {
  const console = useConsole()

  const dictionary = useDictionary()
  const navigation = useNavigation()

  const { variation, variations, isFocused } = useVariations()

  const [showA11y, setShowA11y] = useState(isFocused.get())

  const active = variations.find(({ rmc }) => rmc === variation)
  const rmc = active?.rmc
  // const family = active?.family?.toLowerCase?.()?.replace?.(" ", "-")
  const href = navigation.localize(`/watches/${active.familyCode}/${rmc}`)

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.target.getAttribute("href"))
  }

  function onFocusChange(bool) {
    setShowA11y(bool)
  }
  useEffect(() => isFocused.onChange(onFocusChange))

  console.verbose("FamilyVariations:Infos(%s) {%o}", variation, { variation: active })
  return (
    <Header>
      <div>
        <WishlistButton
          rmc={active.rmc}
          key={active.rmc}
          family={active.familyCode}
          modelName={active.title}
          newmodelselection={active.newmodelselection}
          withLabel
          className='wishlist-button-variations'
        />
      </div>
      <div aria-live='polite'>
        {active && (
          <span id='label' className='hidden'>
            {dictionary.discoverModel()}
          </span>
        )}
        {active && <h2 id='name'>{active.title}</h2>}
        {active && <h3 id='description'>{active.facet_case_title}</h3>}
        <a aria-labelledby='label name description' target='_self' href={href} onClick={navigate} className='reverseIcon'>
          <Icon type={"chevron"} />
          {dictionary.discoverModel()}
        </a>
        <AnimatePresence>
          {showA11y && (
            <A11yKeyMessage
              key='a11y-msg'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "tween", duration: 0.25 }}
            >
              <p>{dictionary.useArrowToRotate()}</p>
              <KeyIconLeft />
              <KeyIconRight />
            </A11yKeyMessage>
          )}
        </AnimatePresence>
      </div>
    </Header>
  )
}
